import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { toast } from 'sonner';
import { User, Mail, Calendar, Camera, LogOut, Lock, Save, ChevronDown } from 'lucide-react';

const ruqaaFont = { fontFamily: "'Aref Ruqaa', 'Amiri', serif" };
const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };

// Country codes for phone input
const countryCodes = [
  { code: '+966', country: 'السعودية', iso: 'sa' },
  { code: '+971', country: 'الإمارات', iso: 'ae' },
  { code: '+965', country: 'الكويت', iso: 'kw' },
  { code: '+974', country: 'قطر', iso: 'qa' },
  { code: '+973', country: 'البحرين', iso: 'bh' },
  { code: '+968', country: 'عمان', iso: 'om' },
  { code: '+20', country: 'مصر', iso: 'eg' },
  { code: '+249', country: 'السودان', iso: 'sd' },
  { code: '+962', country: 'الأردن', iso: 'jo' },
  { code: '+970', country: 'فلسطين', iso: 'ps' },
  { code: '+961', country: 'لبنان', iso: 'lb' },
  { code: '+963', country: 'سوريا', iso: 'sy' },
  { code: '+964', country: 'العراق', iso: 'iq' },
  { code: '+967', country: 'اليمن', iso: 'ye' },
  { code: '+212', country: 'المغرب', iso: 'ma' },
  { code: '+216', country: 'تونس', iso: 'tn' },
  { code: '+213', country: 'الجزائر', iso: 'dz' },
  { code: '+218', country: 'ليبيا', iso: 'ly' },
];

const getFlagUrl = (iso: string) => `https://flagcdn.com/w40/${iso}.png`;

// Helper to split display name
const splitDisplayName = (displayName: string | undefined) => {
  if (!displayName) return { firstName: '', lastName: '' };
  const parts = displayName.trim().split(' ');
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || ''
  };
};

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  
  // Initialize form with user data
  const { firstName, lastName } = splitDisplayName(user?.displayName);
  
  const [formData, setFormData] = useState({
    firstName: firstName,
    lastName: lastName,
    username: user?.username || '',
    email: user?.email || '',
    phone: '',
    countryCode: '+966',
    birthDate: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Update form when user changes
  useEffect(() => {
    if (user) {
      const { firstName, lastName } = splitDisplayName(user.displayName);
      setFormData(prev => ({
        ...prev,
        firstName,
        lastName,
        username: user.username || '',
        email: user.email || '',
      }));
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Combine first and last name for displayName
      const displayName = `${formData.firstName} ${formData.lastName}`.trim();
      
      // TODO: Call API to update profile in database
      // For now, update local state
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update auth store with new display name
      updateUser({ displayName });
      
      toast.success('تم حفظ التغييرات بنجاح');
    } catch (error) {
      toast.error('حدث خطأ أثناء حفظ التغييرات');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('كلمة المرور الجديدة غير متطابقة');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // TODO: Call API to change password
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('تم تغيير كلمة المرور بنجاح');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error('حدث خطأ أثناء تغيير كلمة المرور');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);

  return (
    <div className="min-h-screen bg-gray-50 py-12" style={arabicFont}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <h1 className="text-5xl font-bold text-green-600 text-center mb-12" style={ruqaaFont}>
            حسابي
          </h1>

          {/* Main Card */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex-1 py-4 text-lg font-bold transition-colors ${
                  activeTab === 'profile'
                    ? 'bg-gray-100 text-gray-800 border-b-2 border-gray-800'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                حساب تعريفي
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`flex-1 py-4 text-lg font-bold transition-colors ${
                  activeTab === 'password'
                    ? 'bg-gray-100 text-gray-800 border-b-2 border-gray-800'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                تغيير كلمة المرور
              </button>
            </div>

            <div className="p-8">
              <div className="flex flex-col md:flex-row gap-12">
                {/* Profile Picture Section */}
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative">
                    <div className="w-32 h-32 bg-green-600 rounded-full flex items-center justify-center">
                      <User className="w-16 h-16 text-white" />
                    </div>
                    <button className="absolute bottom-0 right-0 w-10 h-10 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors">
                      <Camera className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-gray-800 text-lg">
                      {user?.displayName || user?.username}
                    </p>
                    <p className="text-gray-500 text-sm">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-6 py-2 border-2 border-green-600 text-green-600 hover:bg-green-50 rounded-full font-bold transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    تسجيل خروج
                  </button>
                </div>

                {/* Form Section */}
                <div className="flex-1">
                  {activeTab === 'profile' ? (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2">الاسم الأول</label>
                          <Input
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            className="w-full border-gray-300 rounded-lg py-3"
                            placeholder="الاسم الأول"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2">الاسم الأخير</label>
                          <Input
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            className="w-full border-gray-300 rounded-lg py-3"
                            placeholder="الاسم الأخير"
                          />
                        </div>
                      </div>

                      {/* Username Field (Read-only) */}
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">اسم المستخدم</label>
                        <div className="relative">
                          <Input
                            value={formData.username}
                            className="w-full border-gray-300 rounded-lg py-3 pl-10 bg-gray-50"
                            readOnly
                            dir="ltr"
                          />
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">لا يمكن تغيير اسم المستخدم</p>
                      </div>

                      {/* Phone Field */}
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">رقم الهاتف</label>
                        <div className="flex gap-2">
                          <Input
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="flex-1 border-gray-300 rounded-lg py-3"
                            placeholder="رقم الهاتف"
                            dir="ltr"
                          />
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                              className="flex items-center gap-2 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors min-w-[160px]"
                            >
                              {selectedCountry && (
                                <img 
                                  src={getFlagUrl(selectedCountry.iso)} 
                                  alt={selectedCountry.country}
                                  className="w-6 h-4 object-cover rounded"
                                />
                              )}
                              <span className="text-gray-700 font-medium">{selectedCountry?.country}</span>
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                            
                            {showCountryDropdown && (
                              <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
                                {countryCodes.map((country) => (
                                  <button
                                    key={country.code}
                                    type="button"
                                    onClick={() => {
                                      setFormData({ ...formData, countryCode: country.code });
                                      setShowCountryDropdown(false);
                                    }}
                                    className="flex items-center justify-between w-full px-4 py-3 hover:bg-gray-50 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <img 
                                        src={getFlagUrl(country.iso)} 
                                        alt={country.country}
                                        className="w-6 h-4 object-cover rounded"
                                      />
                                      <span className="text-gray-700">{country.country}</span>
                                    </div>
                                    <span className="text-gray-500 text-sm">{country.code}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Email Field (Read-only) */}
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">البريد الإلكتروني</label>
                        <div className="relative">
                          <Input
                            type="email"
                            value={formData.email}
                            className="w-full border-gray-300 rounded-lg py-3 pl-10 bg-gray-50"
                            readOnly
                            dir="ltr"
                          />
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">لا يمكن تغيير البريد الإلكتروني</p>
                      </div>

                      {/* Birth Date Field */}
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">تاريخ الميلاد</label>
                        <div className="relative">
                          <Input
                            type="date"
                            value={formData.birthDate}
                            onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                            className="w-full border-gray-300 rounded-lg py-3 pl-10"
                          />
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 rounded-xl font-bold text-lg"
                      >
                        {isSubmitting ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <Save className="w-5 h-5 ml-2" />
                            حفظ التغييرات
                          </>
                        )}
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handlePasswordChange} className="space-y-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">كلمة المرور الحالية</label>
                        <div className="relative">
                          <Input
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            className="w-full border-gray-300 rounded-lg py-3 pl-10"
                            placeholder="كلمة المرور الحالية"
                          />
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">كلمة المرور الجديدة</label>
                        <div className="relative">
                          <Input
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                            className="w-full border-gray-300 rounded-lg py-3 pl-10"
                            placeholder="كلمة المرور الجديدة"
                          />
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">تأكيد كلمة المرور الجديدة</label>
                        <div className="relative">
                          <Input
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                            className="w-full border-gray-300 rounded-lg py-3 pl-10"
                            placeholder="تأكيد كلمة المرور الجديدة"
                          />
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 rounded-xl font-bold text-lg"
                      >
                        {isSubmitting ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <Lock className="w-5 h-5 ml-2" />
                            تغيير كلمة المرور
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Social Media */}
            <div className="flex items-center gap-4">
              <a href="#" className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/></svg>
              </a>
              <a href="#" className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
              </a>
              <a href="#" className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="#" className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>

            {/* Copyright */}
            <p className="text-gray-500 text-sm">
              Copyright 2026 اعصر مخك - All Rights Reserved ©
            </p>

            {/* App Store Links */}
            <div className="flex items-center gap-4">
              <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/></svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
